import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Link,
  Divider,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { blue, grey, orange } from '@mui/material/colors';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import UICard, { UICardContent } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';
import { enrollReseller } from './utils/connectors';
import { useResellerContext } from './utils/contexts';

// Form steps
const steps = ['Business Information', 'Contact Details', 'Agreement & Submit'];

export default function ResellerEnrollForm() {
  const navigate = useNavigate();
  const { refreshCustomers, refreshStats } = useResellerContext();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Business Information
    businessName: '',
    businessType: '',
    website: '',
    yearFounded: '',
    employeeCount: '',
    
    // Contact Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    
    // Agreement
    acceptTerms: false,
    marketingConsent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error if field was updated
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    
    // Validate based on current step
    if (activeStep === 0) {
      if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
      if (!formData.businessType) newErrors.businessType = 'Business type is required';
      if (!formData.website.trim()) newErrors.website = 'Website is required';
    } else if (activeStep === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
        newErrors.email = 'Valid email is required';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    } else if (activeStep === 2) {
      if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        setSubmitting(true);
        setSubmitError(null);
        // Send data to API
        const response = await enrollReseller(formData);
        
        if (response.success) {
          setSubmitSuccess(true);
          
          // Refresh customer data after successful enrollment
          await Promise.all([
            refreshCustomers(),
            refreshStats()
          ]);
          
          // Redirect after successful submission (after showing success message)
          setTimeout(() => {
            navigate('/resellers');
          }, 3000);
        } else {
          setSubmitError(response.message || 'Failed to submit the form. Please try again.');
        }
      } catch (error) {
        setSubmitError('An unexpected error occurred. Please try again later.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const renderBusinessInformationStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Business Name</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.businessName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('businessName', e.target.value)}
          error={!!errors.businessName}
          helperText={errors.businessName}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Business Type</Typography>
        </Box>
        <Utils.FormUtils.PrimarySelect
          value={formData.businessType}
          onChange={(e) => handleInputChange('businessType', e.target.value as string)}
          error={!!errors.businessType}
          fullWidth
        >
          <MenuItem value="" disabled>Select business type</MenuItem>
          <Utils.FormUtils.PrimarySelectItem value="MSP">Managed Service Provider</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="VAR">Value-Added Reseller</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="SI">System Integrator</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="ISV">Independent Software Vendor</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="Other">Other</Utils.FormUtils.PrimarySelectItem>
        </Utils.FormUtils.PrimarySelect>
        {errors.businessType && <Typography color="error" fontSize={12}>{errors.businessType}</Typography>}
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Company Website</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.website}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('website', e.target.value)}
          error={!!errors.website}
          helperText={errors.website}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Year Founded</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.yearFounded}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('yearFounded', e.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Number of Employees</Typography>
        </Box>
        <Utils.FormUtils.PrimarySelect
          value={formData.employeeCount}
          onChange={(e) => handleInputChange('employeeCount', e.target.value as string)}
          fullWidth
        >
          <MenuItem value="" disabled>Select number of employees</MenuItem>
          <Utils.FormUtils.PrimarySelectItem value="1-10">1-10</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="11-50">11-50</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="51-200">51-200</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="201-1000">201-1000</Utils.FormUtils.PrimarySelectItem>
          <Utils.FormUtils.PrimarySelectItem value="1000+">More than 1000</Utils.FormUtils.PrimarySelectItem>
        </Utils.FormUtils.PrimarySelect>
      </Grid>
    </Grid>
  );

  const renderContactDetailsStep = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>First Name</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('firstName', e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Last Name</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('lastName', e.target.value)}
          error={!!errors.lastName}
          helperText={errors.lastName}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Email Address</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Phone Number</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
          error={!!errors.phone}
          helperText={errors.phone}
          required
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Box mb={1}>
          <Typography fontWeight={600} fontSize={13}>Job Position</Typography>
        </Box>
        <Utils.FormUtils.PrimaryInput
          value={formData.position}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('position', e.target.value)}
          fullWidth
        />
      </Grid>
    </Grid>
  );

  const renderAgreementStep = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box 
            sx={{ 
              p: 4, 
              borderRadius: 2, 
              border: `1px solid ${grey[200]}`,
              bgcolor: 'background.paper',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Typography variant="h6" fontWeight={500} fontSize={16} letterSpacing={0.75} gutterBottom>
              Notice on Agreement Updates
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontSize={14} paragraph>
                Dear Partner, we have recently updated the terms in the Reseller Partner Program Agreement. The updates include:
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontSize={14} paragraph>
                  By enrolling in our Reseller Program, you agree to our <Link href="#" color="primary" sx={{ fontWeight: 500 }}>Partner Terms and Conditions</Link>, which include provisions related to confidentiality, intellectual property, commission structure, and service level expectations.
                </Typography>
                
                <Typography variant="body2" fontSize={14} paragraph>
                  We are notifying you of the updates to display the relevant terms and conditions. Please carefully read through the <Link href="#" color="primary" sx={{ fontWeight: 500 }}>Product Terms</Link>, <Link href="#" color="primary" sx={{ fontWeight: 500 }}>Membership Agreement</Link>, <Link href="#" color="primary" sx={{ fontWeight: 500 }}>Privacy Policy</Link>, and <Link href="#" color="primary" sx={{ fontWeight: 500 }}>Website Terms of Use</Link> and ensure that you understand and confirm these terms.
                </Typography>
                
                <Typography variant="body2" fontSize={14}>
                  As a partner, you will have access to our partner portal, marketing resources, and dedicated support. You will earn commission on qualifying sales to customers you bring to our platform.
                </Typography>
                
                <Typography variant="body2" sx={{ mt: 2 }} fontSize={14}>
                  The initial partnership term is 12 months, renewable based on performance and compliance with program requirements. If you have any questions, please feel free to contact us.
                </Typography>
              </Box>
              
              <Typography variant="body2" fontSize={14} fontWeight={500}>
                Thank you.
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      sx={{ 
                        color: orange[800],
                        '&.Mui-checked': {
                          color: orange[600],
                        }
                      }}
                    />
                  }
                  label={<Typography fontSize={14}>I accept the terms and conditions of the Reseller Program</Typography>}
                />
                {errors.acceptTerms && <Typography color="error" fontSize={12}>{errors.acceptTerms}</Typography>}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.marketingConsent}
                      onChange={(e) => handleInputChange('marketingConsent', e.target.checked)}
                      sx={{ 
                        color: blue[800],
                        '&.Mui-checked': {
                          color: blue[600],
                        }
                      }}
                    />
                  }
                  label={<Typography fontSize={14}>I consent to receiving marketing materials about partner opportunities</Typography>}
                />
              </Grid>
            </Grid>
            
            {errors.submit && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Alert severity="error">{errors.submit}</Alert>
              </Grid>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box 
            sx={{ 
              p: 3, 
              borderRadius: 2, 
              border: `1px solid ${grey[200]}`,
              bgcolor: 'background.paper',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
              height: '100%'
            }}
          >
            <Typography variant="h6" fontWeight={500} fontSize={16} letterSpacing={0.75} gutterBottom>
              Account Benefits
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CalendarTodayOutlinedIcon sx={{ color: orange[600], mt: 0.5 }} fontSize="small" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} fontSize={14} gutterBottom>
                    Free Trial
                  </Typography>
                  <Typography variant="body2" fontSize={14}>
                    Get free hands-on experience with 50+ products. Now up to 12 Months for Elastic Compute Service!
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <SupportAgentOutlinedIcon sx={{ color: orange[600], mt: 0.5 }} fontSize="small" />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} fontSize={14} gutterBottom>
                    Premium Support Services
                  </Typography>
                  <Typography variant="body2" fontSize={14}>
                    1-on-1 pre-sale consultation, 24/7 after-sales technical support with 6 free tickets per quarter
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Utils.FormUtils.PrimaryButton
          onClick={handlePrevStep}
          disabled={activeStep === 0 || submitting}
          variant="outlined"
          light={true}
          sx={{ 
            px: 3, 
            fontSize: 14,
            fontWeight: 500
          }}
        >
          Back
        </Utils.FormUtils.PrimaryButton>
        
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Utils.FormUtils.PrimaryButton
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formData.acceptTerms}
            fullWidth
            sx={{ py: 1.5 }}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Utils.FormUtils.PrimaryButton>
        </Grid>
      </Box>
      
      {submitSuccess && (
        <Grid item xs={12}>
          <Alert severity="success" sx={{ mt: 2 }}>
            Your application has been submitted successfully! You will be redirected to the dashboard.
          </Alert>
        </Grid>
      )}
      
      {submitError && (
        <Grid item xs={12}>
          <Alert 
            severity="error" 
            sx={{ mt: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        </Grid>
      )}
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBusinessInformationStep();
      case 1:
        return renderContactDetailsStep();
      case 2:
        return renderAgreementStep();
      default:
        return null;
    }
  };

  const { sidebar } = useResellerContext();

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            <Box mb={4}>
              <Utils.LayoutUtils.MainHeading
                title="Reseller Program Enrollment"
                description="Complete the form below to apply for our Reseller Partner Program"
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UICard>
                  <UICardContent>
                    <Box p={2}>
                      {submitSuccess ? (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="body1" fontWeight={500} fontSize={15}>
                            Your application has been submitted successfully!
                          </Typography>
                          <Typography variant="body2" fontSize={14}>
                            Our partner team will review your application and contact you within 2-3 business days.
                          </Typography>
                        </Alert>
                      ) : (
                        <>
                          <Stepper 
                            activeStep={activeStep} 
                            sx={{ 
                              mb: 4,
                              '& .MuiStepLabel-label': {
                                fontSize: 14,
                                fontWeight: activeStep === 2 ? 500 : 400
                              },
                              '& .MuiStepLabel-label.Mui-active': {
                                fontWeight: 500,
                                color: orange[800]
                              },
                              '& .MuiStepIcon-root.Mui-active': {
                                color: orange[600]
                              },
                              '& .MuiStepIcon-root.Mui-completed': {
                                color: orange[600]
                              }
                            }}
                          >
                            {steps.map((label) => (
                              <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                              </Step>
                            ))}
                          </Stepper>
                          
                          {renderStepContent()}
                          
                          {activeStep < 2 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                              <Utils.FormUtils.PrimaryButton
                                onClick={handlePrevStep}
                                disabled={activeStep === 0 || submitting}
                                variant="outlined"
                                sx={{ 
                                  px: 3, 
                                  fontSize: 14,
                                  fontWeight: 500
                                }}
                              >
                                Back
                              </Utils.FormUtils.PrimaryButton>
                              
                              <Utils.FormUtils.PrimaryButton
                                onClick={handleNextStep}
                                disabled={submitting}
                                sx={{ 
                                  px: 3, 
                                  fontSize: 14,
                                  fontWeight: 500
                                }}
                              >
                                Next
                              </Utils.FormUtils.PrimaryButton>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </UICardContent>
                </UICard>
              </Grid>
            </Grid>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>
    </Grid>
  );
} 